<!-- This component is used to show an upselling banner on specific pages. -->
<template>
  <div v-if="visible"
    :class="{ 'upselling-page-banner-layer': true, 'margin-for-tab-panel': routeUsesTabPanel, 'margin-for-no-tab-panel': !routeUsesTabPanel }"
    :style="{ 'margin-left': `${sidebarOffset}px` }"
  >
    <div class="upselling-page-banner-backdrop" />
    <InfoBanner
      class="upselling-page-banner"
      :title="infoBannerDetails.title"
      :details-list="infoBannerDetails.detailsList"
      :details-paragraph="infoBannerDetails.detailsParagraph"
      :additional-details="infoBannerDetails.additionalDetails"
      :primaryCta="infoBannerDetails.primaryCta "
      :secondaryCta="{ text: 'Learn More', action: () => handleSecondaryCta(
        infoBannerDetails,
        () => isVideoPlayerModalVisible = true
      )}"
    />
    <VideoPlayerModal 
      v-model="isVideoPlayerModalVisible"
      :video-url="infoBannerDetails.secondaryCtaVideoUrl"
      :title="infoBannerDetails.title"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import InfoBanner from '@/components/InfoBanner.vue'
import { useRoute, useRouter } from 'vue-router'
import { ROUTE_UPSELLING_DETAILS, handleSecondaryCta } from '@/utils/upselling'
import VideoPlayerModal from './VideoPlayerModal.vue';
import { useStore } from 'vuex'
import { useUpselling } from '@/composables/useUpselling.ts'

const store = useStore()
const route = useRoute()
const router = useRouter()
const sidebarOffset = ref(280)
const DESKTOP_SIDEBAR_WIDTH = 280
const MOBILE_SIDEBAR_WIDTH = 280
const MINIMIZED_SIDEBAR_WIDTH = 50
const MOBILE_BREAKPOINT_QUERY = '(max-width: 992px)' //992px is bootstrap's lg breakpoint, which is when the sidebar switches to mobile view

let bodyClassObserver: MutationObserver | null = null

const isMobileViewport = () => window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches

const updateSidebarOffset = () => {
  const bodyClasses = document.body.classList

  if (isMobileViewport()) {
    sidebarOffset.value = bodyClasses.contains('sidebar-mobile-show') ? MOBILE_SIDEBAR_WIDTH : 0
    return
  }

  if (bodyClasses.contains('sidebar-hidden')) {
    sidebarOffset.value = 0
    return
  }

  if (bodyClasses.contains('sidebar-minimized')) {
    sidebarOffset.value = MINIMIZED_SIDEBAR_WIDTH
    return
  }

  sidebarOffset.value = DESKTOP_SIDEBAR_WIDTH
}

const isVideoPlayerModalVisible = ref(false)

const { infoBannerDetails } = useUpselling({
  resolveDetail: () => ROUTE_UPSELLING_DETAILS.find(detail => detail.routeName?.includes(route.name as string))
})


// Check if any matched route satisfies the ff:
// 1. includes the current route as a child and uses TabPanel component
// 2. uses TabPanel component in any of its components (default or named)
// This is to determine if we need to add margin to the banner to avoid covering the tabs
const routeUsesTabPanel = computed(() => {
  if (!route.name) {
    return false
  }

  return route.matched.some((record) => {
    const hasCurrentRouteAsChild = (record.children ?? []).some((child) => child.name === route.name)
    if (!hasCurrentRouteAsChild) {
      return false
    }

    return Object.values(record.components ?? {}).some(component => {
      if (!component) {
        return false
      }

      if (typeof component === 'function') {
        return component.toString().includes('TabPanel.vue')
      }

      return component.__file?.includes('/TabPanel.vue')
    })
  })
})

// Check if the current route matches any of the routes that we want to upsell
const matchedDetail = computed(() =>
  ROUTE_UPSELLING_DETAILS.find(detail =>
    detail.routeName?.includes(route.name as string)
  )
)

const visible = computed(() => {
  // Visible if there is a matched detail for the current route
  // and the feature is not included in the features
  return !!matchedDetail.value && !store.state.features.includes(matchedDetail.value.feature)
})

watch(route, () => {
  if(!visible.value) {
    return
  }
  const matchedRedirectTo = (matchedDetail.value?.redirectTo ?? []).find(redirect => redirect.name === route.name)
  if (matchedRedirectTo) {
    router.push(matchedRedirectTo)
  }
}, { immediate: true })

onMounted(() => {
  updateSidebarOffset()
  bodyClassObserver = new MutationObserver(updateSidebarOffset)
  bodyClassObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class']
  })
  window.addEventListener('resize', updateSidebarOffset)
})

onBeforeUnmount(() => {
  bodyClassObserver?.disconnect()
  bodyClassObserver = null
  window.removeEventListener('resize', updateSidebarOffset)
})

</script>

<style lang="css" scoped>

.upselling-page-banner-layer {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  transition: margin-left .25s;
  will-change: margin-left;
}

.margin-for-tab-panel {
  margin-top: 94px; /* Add margin to show header + tab panels */
}

.margin-for-no-tab-panel {
  margin-top: 55px; /* Add margin to show header */
}

@media (prefers-reduced-motion: reduce) {
  .upselling-page-banner-layer {
    transition: none;
  }
}

.upselling-page-banner-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(8, 10, 20, 0.5);
}

.upselling-page-banner {
  z-index: 1;
  position: relative;
  width: var(--upselling-banner-width);
}
</style>
